import React, { ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import { ArrowForward, ArrowBack, Close } from '@mui/icons-material';
import { gray100, gray80, primaryButtonBackgroundColor } from '../../../constants/colors';
import { boldXxl, regularLg, regularMd } from '../../../constants/location';
import logo from '../../assets/logo_icon.png';
import tutorialLast from '../../assets/tutorial_last.png';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';

// Firebase Storage のチュートリアル動画URL
const storageBucket = process.env.REACT_APP_GCP_STORAGE_BUCKET;
const getTutorialVideoUrl = (fileName: string): string => {
  return `https://firebasestorage.googleapis.com/v0/b/${storageBucket}/o/tutorials%2F${fileName}?alt=media`;
};

interface TutorialModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

// チュートリアル動画のファイル名リスト
const TUTORIAL_VIDEOS = ['tutorial_01.mp4', 'tutorial_02.mp4', 'tutorial_03.mp4'];

const TutorialModal = ({ open, onClose, onComplete }: TutorialModalProps): ReactElement => {
  const proxyDialogSx = useProxyDialogSx();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [videoLoaded, setVideoLoaded] = useState<Record<number, boolean>>({});

  // モーダルが開いた時に全動画をプリロード
  useEffect(() => {
    if (open) {
      TUTORIAL_VIDEOS.forEach(fileName => {
        const video = document.createElement('video');
        video.preload = 'auto';
        video.src = getTutorialVideoUrl(fileName);
        video.load();
      });
    }
  }, [open]);

  const handleNext = (): void => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = (): void => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = (): void => {
    setCurrentStep(1);
    onComplete();
  };

  const handleClose = (): void => {
    setCurrentStep(1);
    onClose();
  };

  const styles = {
    dialog: {
      '& .MuiDialog-paper': {
        maxWidth: currentStep === 1 || currentStep === 5 ? '560px' : '100%',
        width: '100%',
        height: currentStep >= 2 && currentStep <= 4 ? '100%' : 'auto',
        maxHeight: currentStep >= 2 && currentStep <= 4 ? '100%' : 'none',
        borderRadius: currentStep >= 2 && currentStep <= 4 ? 0 : '8px',
        padding: 0,
        margin: currentStep >= 2 && currentStep <= 4 ? 0 : '32px',
        '@media (max-width: 960px)': {
          margin: currentStep >= 2 && currentStep <= 4 ? 0 : '16px',
        },
      },
    },
    dialogContent: {
      padding:
        currentStep === 1 || currentStep === 5
          ? '48px'
          : currentStep >= 2 && currentStep <= 4
            ? 0
            : '40px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      height: currentStep >= 2 && currentStep <= 4 ? '100%' : 'auto',
      backgroundColor: currentStep >= 2 && currentStep <= 4 ? '#f6f7f8' : 'white',
      '@media (max-width: 960px)': {
        padding:
          currentStep === 1 || currentStep === 5
            ? '24px'
            : currentStep >= 2 && currentStep <= 4
              ? 0
              : '24px',
      },
    },
    closeButton: {
      position: 'absolute',
      right: '16px',
      top: '16px',
      color: gray80,
    },
    title: {
      ...boldXxl,
      color: gray100,
      textAlign: 'center',
      marginBottom: '16px',
    },
    subtitle: {
      ...regularLg,
      color: gray80,
      textAlign: 'center',
      marginBottom: '8px',
    },
    description: {
      ...regularMd,
      color: gray100,
      textAlign: 'center',
      marginBottom: '32px',
    },
    videoPlaceholder: {
      width: '100%',
      height: '450px',
      backgroundColor: '#f0f0f0',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '32px',
      color: gray80,
    },
    navigationContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      marginTop: '24px',
    },
    progressContainer: {
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
    },
    progressDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: gray80,
      '&.active': {
        backgroundColor: primaryButtonBackgroundColor.backgroundColor,
      },
    },
    buttonContainer: {
      display: 'flex',
      gap: '12px',
    },
    primaryButton: {
      ...primaryButtonBackgroundColor,
      height: '44px',
      padding: '0 24px',
      minWidth: '120px',
    },
    secondaryButton: {
      height: '44px',
      padding: '0 24px',
      minWidth: '120px',
      backgroundColor: 'transparent',
      color: '#171b4b',
      border: '1px solid rgba(23, 27, 75, 0.6)',
      '&:hover': {
        backgroundColor: 'rgba(23, 27, 75, 0.04)',
      },
    },
  };

  const getTutorialContent = (): ReactElement => {
    switch (currentStep) {
      case 1:
        // Step 1: ウェルカムモーダル
        return (
          <>
            <Box
              component="img"
              src={logo}
              alt="Gen Drive"
              sx={{
                width: '48px',
                height: '48px',
                marginBottom: '24px',
              }}
            />
            <Typography
              sx={{
                fontSize: '22px',
                fontWeight: 500,
                color: gray100,
                textAlign: 'center',
                marginBottom: '24px',
                lineHeight: 1.5,
              }}
            >
              Gen Driveへようこそ
            </Typography>
            <Typography
              sx={{
                fontSize: '16px',
                color: gray100,
                textAlign: 'center',
                marginBottom: '32px',
                lineHeight: 1.5,
              }}
            >
              まずは、AIを使ったデータ化の流れを
              <br />
              体験してみましょう。
            </Typography>
            <Button
              sx={{
                ...primaryButtonBackgroundColor,
                height: '44px',
                padding: '0 32px',
                minWidth: '248px',
                fontSize: '16px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                '@media (max-width: 960px)': {
                  minWidth: '0',
                  width: '100%',
                  padding: '0 16px',
                },
              }}
              onClick={handleNext}
            >
              ツアーをはじめる
              <ArrowForward sx={{ fontSize: '24px' }} />
            </Button>
          </>
        );

      case 2:
        // Step 2: 全画面チュートリアル①
        return (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* プログレスインジケーター */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: '48px',
                marginBottom: '40px',
                '@media (max-width: 960px)': {
                  paddingTop: '24px',
                  marginBottom: '24px',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#0a0a0a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px',
                    fontWeight: 700,
                    '@media (max-width: 960px)': {
                      width: '32px',
                      height: '32px',
                      fontSize: '18px',
                    },
                  }}
                >
                  1
                </Box>
                <Box
                  sx={{
                    width: '180px',
                    height: '2px',
                    backgroundColor: '#a4b1b9',
                    '@media (max-width: 960px)': { width: '60px' },
                  }}
                />
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#d9d9d9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px',
                    fontWeight: 700,
                    '@media (max-width: 960px)': {
                      width: '32px',
                      height: '32px',
                      fontSize: '18px',
                    },
                  }}
                >
                  2
                </Box>
                <Box
                  sx={{
                    width: '180px',
                    height: '2px',
                    backgroundColor: '#a4b1b9',
                    '@media (max-width: 960px)': { width: '60px' },
                  }}
                />
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#d9d9d9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px',
                    fontWeight: 700,
                    '@media (max-width: 960px)': {
                      width: '32px',
                      height: '32px',
                      fontSize: '18px',
                    },
                  }}
                >
                  3
                </Box>
              </Box>
            </Box>

            {/* タイトル */}
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: 600,
                color: gray100,
                textAlign: 'center',
                marginBottom: '16px',
                '@media (max-width: 960px)': {
                  fontSize: '20px',
                  marginBottom: '12px',
                },
              }}
            >
              「読取パターン」を作成
            </Typography>

            {/* 説明文 */}
            <Typography
              sx={{
                fontSize: '16px',
                color: gray100,
                textAlign: 'center',
                marginBottom: '24px',
                lineHeight: 1.5,
                '@media (max-width: 960px)': {
                  fontSize: '14px',
                  marginBottom: '16px',
                  paddingX: '16px',
                },
              }}
            >
              GenDriveでは、最初に「書類の読み方（読取パターン）」をAIに教えます。
              <br />
              このボタンから、読取パターンを作ってみましょう。
            </Typography>

            {/* チュートリアル動画エリア */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingX: '32px',
                marginBottom: '0',
                height: 'calc(100vh - 380px)',
                minHeight: '360px',
                '@media (max-width: 960px)': {
                  paddingX: '16px',
                  minHeight: '200px',
                  height: 'calc(100vh - 320px)',
                },
              }}
            >
              {!videoLoaded[2] && (
                <CircularProgress sx={{ color: primaryButtonBackgroundColor.backgroundColor }} />
              )}
              <Box
                key="video-step2"
                component="video"
                autoPlay
                loop
                muted
                playsInline
                onCanPlay={(): void => setVideoLoaded(prev => ({ ...prev, 2: true }))}
                sx={{
                  width: 'auto',
                  maxWidth: '100%',
                  height: '100%',
                  borderRadius: '4px',
                  border: videoLoaded[2] ? '1px solid #dcdcdc' : 'none',
                  display: videoLoaded[2] ? 'block' : 'none',
                }}
              >
                <source src={getTutorialVideoUrl('tutorial_01.mp4')} type="video/mp4" />
              </Box>
            </Box>

            {/* 下部ナビゲーション */}
            <Box
              sx={{
                width: '100%',
                height: '70px',
                backgroundColor: 'white',
                borderTop: '1px solid #dcdcdc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'fixed',
                bottom: 0,
                left: 0,
                '@media (max-width: 960px)': {
                  height: '64px',
                  paddingX: '16px',
                  boxSizing: 'border-box',
                },
              }}
            >
              <Typography
                component="button"
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  left: '32px',
                  fontSize: '16px',
                  color: '#45556c',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  border: 'none',
                  '&:hover': {
                    opacity: 0.8,
                  },
                  '@media (max-width: 960px)': {
                    position: 'static',
                    fontSize: '14px',
                    marginRight: '12px',
                    flexShrink: 0,
                  },
                }}
              >
                スキップ
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: '12px',
                  '@media (max-width: 960px)': {
                    flex: 1,
                    gap: '8px',
                    justifyContent: 'flex-end',
                  },
                }}
              >
                <Button
                  sx={{
                    ...primaryButtonBackgroundColor,
                    height: '36px',
                    padding: '0 12px 0 12px',
                    minWidth: '160px',
                    fontSize: '14px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: '#171b4b',
                    '&:hover': {
                      backgroundColor: '#0f1235',
                    },
                    '@media (max-width: 960px)': {
                      minWidth: '0',
                      flex: 1,
                      maxWidth: '50%',
                    },
                  }}
                  onClick={handleNext}
                >
                  <Box sx={{ width: '24px', visibility: 'hidden' }} />
                  <Box component="span" sx={{ flex: 1, textAlign: 'center' }}>
                    次へ
                  </Box>
                  <ArrowForward sx={{ fontSize: '24px' }} />
                </Button>
              </Box>
            </Box>
          </Box>
        );

      case 3:
        // Step 3: 全画面チュートリアル②
        return (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* プログレスインジケーター */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: '48px',
                marginBottom: '40px',
                '@media (max-width: 960px)': {
                  paddingTop: '24px',
                  marginBottom: '24px',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#0a0a0a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px',
                    fontWeight: 700,
                    '@media (max-width: 960px)': {
                      width: '32px',
                      height: '32px',
                      fontSize: '18px',
                    },
                  }}
                >
                  1
                </Box>
                <Box
                  sx={{
                    width: '180px',
                    height: '2px',
                    backgroundColor: '#a4b1b9',
                    '@media (max-width: 960px)': { width: '60px' },
                  }}
                />
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#0a0a0a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px',
                    fontWeight: 700,
                    '@media (max-width: 960px)': {
                      width: '32px',
                      height: '32px',
                      fontSize: '18px',
                    },
                  }}
                >
                  2
                </Box>
                <Box
                  sx={{
                    width: '180px',
                    height: '2px',
                    backgroundColor: '#a4b1b9',
                    '@media (max-width: 960px)': { width: '60px' },
                  }}
                />
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#d9d9d9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px',
                    fontWeight: 700,
                    '@media (max-width: 960px)': {
                      width: '32px',
                      height: '32px',
                      fontSize: '18px',
                    },
                  }}
                >
                  3
                </Box>
              </Box>
            </Box>

            {/* タイトル */}
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: 600,
                color: gray100,
                textAlign: 'center',
                marginBottom: '16px',
                '@media (max-width: 960px)': {
                  fontSize: '20px',
                  marginBottom: '12px',
                },
              }}
            >
              ファイルアップロード
            </Typography>

            {/* 説明文 */}
            <Typography
              sx={{
                fontSize: '16px',
                color: gray100,
                textAlign: 'center',
                marginBottom: '24px',
                lineHeight: 1.5,
                '@media (max-width: 960px)': {
                  fontSize: '14px',
                  marginBottom: '16px',
                  paddingX: '16px',
                },
              }}
            >
              書類をアップロードし、読取項目をAIが自動で設定。
              <br />
              それぞれの読取内容に沿ってパターンが作成されます。
            </Typography>

            {/* チュートリアル動画エリア */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingX: '32px',
                marginBottom: '100px',
                height: 'calc(100vh - 380px)',
                minHeight: '360px',
                '@media (max-width: 960px)': {
                  paddingX: '16px',
                  minHeight: '200px',
                  height: 'calc(100vh - 320px)',
                  marginBottom: '80px',
                },
              }}
            >
              {!videoLoaded[3] && (
                <CircularProgress sx={{ color: primaryButtonBackgroundColor.backgroundColor }} />
              )}
              <Box
                key="video-step3"
                component="video"
                autoPlay
                loop
                muted
                playsInline
                onCanPlay={(): void => setVideoLoaded(prev => ({ ...prev, 3: true }))}
                sx={{
                  width: 'auto',
                  maxWidth: '100%',
                  height: '100%',
                  borderRadius: '4px',
                  border: videoLoaded[3] ? '1px solid #dcdcdc' : 'none',
                  display: videoLoaded[3] ? 'block' : 'none',
                }}
              >
                <source src={getTutorialVideoUrl('tutorial_02.mp4')} type="video/mp4" />
              </Box>
            </Box>

            {/* 下部ナビゲーション */}
            <Box
              sx={{
                width: '100%',
                height: '70px',
                backgroundColor: 'white',
                borderTop: '1px solid #dcdcdc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'fixed',
                bottom: 0,
                left: 0,
                '@media (max-width: 960px)': {
                  height: '64px',
                  paddingX: '16px',
                  boxSizing: 'border-box',
                },
              }}
            >
              <Typography
                component="button"
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  left: '32px',
                  fontSize: '16px',
                  color: '#45556c',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  border: 'none',
                  '&:hover': {
                    opacity: 0.8,
                  },
                  '@media (max-width: 960px)': {
                    position: 'static',
                    fontSize: '14px',
                    marginRight: '12px',
                    flexShrink: 0,
                  },
                }}
              >
                スキップ
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: '12px',
                  '@media (max-width: 960px)': {
                    flex: 1,
                    gap: '8px',
                  },
                }}
              >
                <Button
                  sx={{
                    height: '36px',
                    padding: '0 12px 0 12px',
                    minWidth: '160px',
                    fontSize: '14px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: 'transparent',
                    color: '#171b4b',
                    border: '1px solid rgba(23, 27, 75, 0.6)',
                    borderRadius: '4px',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(23, 27, 75, 0.04)',
                    },
                    '@media (max-width: 960px)': {
                      minWidth: '0',
                      flex: 1,
                    },
                  }}
                  onClick={handleBack}
                >
                  <ArrowBack sx={{ fontSize: '24px' }} />
                  <Box component="span" sx={{ flex: 1, textAlign: 'center' }}>
                    戻る
                  </Box>
                  <Box sx={{ width: '24px', visibility: 'hidden' }} />
                </Button>
                <Button
                  sx={{
                    ...primaryButtonBackgroundColor,
                    height: '36px',
                    padding: '0 12px 0 12px',
                    minWidth: '160px',
                    fontSize: '14px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: '#171b4b',
                    '&:hover': {
                      backgroundColor: '#0f1235',
                    },
                    '@media (max-width: 960px)': {
                      minWidth: '0',
                      flex: 1,
                    },
                  }}
                  onClick={handleNext}
                >
                  <Box sx={{ width: '24px', visibility: 'hidden' }} />
                  <Box component="span" sx={{ flex: 1, textAlign: 'center' }}>
                    次へ
                  </Box>
                  <ArrowForward sx={{ fontSize: '24px' }} />
                </Button>
              </Box>
            </Box>
          </Box>
        );

      case 4:
        // Step 4: 全画面チュートリアル③
        return (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* プログレスインジケーター */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: '48px',
                marginBottom: '40px',
                '@media (max-width: 960px)': {
                  paddingTop: '24px',
                  marginBottom: '24px',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#0a0a0a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px',
                    fontWeight: 700,
                    '@media (max-width: 960px)': {
                      width: '32px',
                      height: '32px',
                      fontSize: '18px',
                    },
                  }}
                >
                  1
                </Box>
                <Box
                  sx={{
                    width: '180px',
                    height: '2px',
                    backgroundColor: '#a4b1b9',
                    '@media (max-width: 960px)': { width: '60px' },
                  }}
                />
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#0a0a0a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px',
                    fontWeight: 700,
                    '@media (max-width: 960px)': {
                      width: '32px',
                      height: '32px',
                      fontSize: '18px',
                    },
                  }}
                >
                  2
                </Box>
                <Box
                  sx={{
                    width: '180px',
                    height: '2px',
                    backgroundColor: '#a4b1b9',
                    '@media (max-width: 960px)': { width: '60px' },
                  }}
                />
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#0a0a0a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px',
                    fontWeight: 700,
                    '@media (max-width: 960px)': {
                      width: '32px',
                      height: '32px',
                      fontSize: '18px',
                    },
                  }}
                >
                  3
                </Box>
              </Box>
            </Box>

            {/* タイトル */}
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: 600,
                color: gray100,
                textAlign: 'center',
                marginBottom: '16px',
                '@media (max-width: 960px)': {
                  fontSize: '20px',
                  marginBottom: '12px',
                },
              }}
            >
              項目設定
            </Typography>

            {/* 説明文 */}
            <Typography
              sx={{
                fontSize: '16px',
                color: gray100,
                textAlign: 'center',
                marginBottom: '24px',
                lineHeight: 1.5,
                '@media (max-width: 960px)': {
                  fontSize: '14px',
                  marginBottom: '16px',
                  paddingX: '16px',
                },
              }}
            >
              データ化したい項目をAIが自動で提案します。
              <br />
              必要な項目を追加・削除してください。
            </Typography>

            {/* チュートリアル動画エリア */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingX: '32px',
                marginBottom: '100px',
                height: 'calc(100vh - 380px)',
                minHeight: '360px',
                '@media (max-width: 960px)': {
                  paddingX: '16px',
                  minHeight: '200px',
                  height: 'calc(100vh - 320px)',
                  marginBottom: '80px',
                },
              }}
            >
              {!videoLoaded[4] && (
                <CircularProgress sx={{ color: primaryButtonBackgroundColor.backgroundColor }} />
              )}
              <Box
                key="video-step4"
                component="video"
                autoPlay
                loop
                muted
                playsInline
                onCanPlay={(): void => setVideoLoaded(prev => ({ ...prev, 4: true }))}
                sx={{
                  width: 'auto',
                  maxWidth: '100%',
                  height: '100%',
                  borderRadius: '4px',
                  border: videoLoaded[4] ? '1px solid #dcdcdc' : 'none',
                  display: videoLoaded[4] ? 'block' : 'none',
                }}
              >
                <source src={getTutorialVideoUrl('tutorial_03.mp4')} type="video/mp4" />
              </Box>
            </Box>

            {/* 下部ナビゲーション */}
            <Box
              sx={{
                width: '100%',
                height: '70px',
                backgroundColor: 'white',
                borderTop: '1px solid #dcdcdc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'fixed',
                bottom: 0,
                left: 0,
                '@media (max-width: 960px)': {
                  height: '64px',
                  paddingX: '16px',
                  boxSizing: 'border-box',
                },
              }}
            >
              <Typography
                component="button"
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  left: '32px',
                  fontSize: '16px',
                  color: '#45556c',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  border: 'none',
                  '&:hover': {
                    opacity: 0.8,
                  },
                  '@media (max-width: 960px)': {
                    position: 'static',
                    fontSize: '14px',
                    marginRight: '12px',
                    flexShrink: 0,
                  },
                }}
              >
                スキップ
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: '12px',
                  '@media (max-width: 960px)': {
                    flex: 1,
                    gap: '8px',
                  },
                }}
              >
                <Button
                  sx={{
                    height: '36px',
                    padding: '0 12px 0 12px',
                    minWidth: '160px',
                    fontSize: '14px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: 'transparent',
                    color: '#171b4b',
                    border: '1px solid rgba(23, 27, 75, 0.6)',
                    borderRadius: '4px',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(23, 27, 75, 0.04)',
                    },
                    '@media (max-width: 960px)': {
                      minWidth: '0',
                      flex: 1,
                    },
                  }}
                  onClick={handleBack}
                >
                  <ArrowBack sx={{ fontSize: '24px' }} />
                  <Box component="span" sx={{ flex: 1, textAlign: 'center' }}>
                    戻る
                  </Box>
                  <Box sx={{ width: '24px', visibility: 'hidden' }} />
                </Button>
                <Button
                  sx={{
                    ...primaryButtonBackgroundColor,
                    height: '36px',
                    padding: '0 12px 0 12px',
                    minWidth: '160px',
                    fontSize: '14px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: '#171b4b',
                    '&:hover': {
                      backgroundColor: '#0f1235',
                    },
                    '@media (max-width: 960px)': {
                      minWidth: '0',
                      flex: 1,
                    },
                  }}
                  onClick={handleNext}
                >
                  <Box sx={{ width: '24px', visibility: 'hidden' }} />
                  <Box component="span" sx={{ flex: 1, textAlign: 'center' }}>
                    次へ
                  </Box>
                  <ArrowForward sx={{ fontSize: '24px' }} />
                </Button>
              </Box>
            </Box>
          </Box>
        );

      case 5:
        // Step 5: 完了ダイアログ
        return (
          <>
            <Box
              component="img"
              src={tutorialLast}
              alt="完了"
              sx={{
                width: '230px',
                height: '125px',
                marginBottom: '32px',
              }}
            />
            <Typography
              sx={{
                fontSize: '22px',
                fontWeight: 500,
                color: gray100,
                textAlign: 'center',
                marginBottom: '24px',
                lineHeight: 1.5,
              }}
            >
              読取パターンのツアーは以上です。
            </Typography>
            <Typography
              sx={{
                fontSize: '16px',
                color: gray100,
                textAlign: 'center',
                marginBottom: '32px',
                lineHeight: 1.5,
              }}
            >
              新しく読取パターンを作成して、
              <br />
              実際の業務を行いましょう。
            </Typography>
            <Button
              sx={{
                ...primaryButtonBackgroundColor,
                height: '44px',
                padding: '0 16px 0 16px',
                minWidth: '248px',
                fontSize: '16px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
              onClick={handleComplete}
            >
              <Box sx={{ width: '24px', visibility: 'hidden' }} />
              <Box component="span" sx={{ flex: 1, textAlign: 'center' }}>
                はじめる
              </Box>
              <ArrowForward sx={{ fontSize: '24px' }} />
            </Button>
          </>
        );

      default:
        return <></>;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={[styles.dialog, proxyDialogSx]}>
      <DialogContent sx={styles.dialogContent}>
        <IconButton sx={styles.closeButton} onClick={handleClose}>
          <Close />
        </IconButton>
        {getTutorialContent()}
      </DialogContent>
    </Dialog>
  );
};

export default TutorialModal;
